'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';
import { getRouteType } from '@/helpers/routeType';
export interface NavigationParams {
  chainIdentifier?: string;
  marketId?: string;
  marketSlug?: string;
  vaultAddress?: string;
  vaultSlug?: string;
  [key: string]: string | undefined;
}

/** Path-segment keys that are consumed by path manipulation and should not leak into query params. */
const PATH_PARAM_KEYS = new Set(['chainIdentifier', 'marketId', 'marketSlug', 'vaultAddress', 'vaultSlug']);
export interface GenerateUrlInput {
  pathname: string;
  searchParams: URLSearchParams;
  params: NavigationParams;
  includeCurrentSearchParams?: boolean;
}

/**
 * Pure URL generation logic — extracted for testability.
 *
 * Route-aware: on multi-chain pages, ignores chainIdentifier param to avoid
 * corrupting the URL. For vault/market detail navigation on multi-chain pages,
 * callers should use getVaultUrl/getMarketUrl directly.
 */
export function generateUrlFromPathname({
  pathname,
  searchParams,
  params,
  includeCurrentSearchParams = true
}: GenerateUrlInput): string {
  const isMultiChain = getRouteType(pathname) === 'multi-chain';
  const pathParts = pathname.split('/').filter(Boolean);
  let newPathParts = [...pathParts];

  // On multi-chain pages, path stays the same (chainIdentifier changes are ignored).
  if (!isMultiChain) {
    if (params.chainIdentifier) newPathParts[0] = params.chainIdentifier;
    if (params.marketId) newPathParts[2] = params.marketId;
    if (params.marketSlug) newPathParts[3] = params.marketSlug;
    if (params.vaultAddress) newPathParts[2] = params.vaultAddress;
    if (params.vaultSlug) newPathParts[3] = params.vaultSlug;

    // Rebuild path for detail pages. Assumes 4-segment structure:
    // /chain/section/id/slug — trailing segments beyond index 3 are dropped.
    if (params.marketId || params.marketSlug) {
      newPathParts = [
      // oxlint-disable-next-line typescript/consistent-type-assertions
      newPathParts[0] as string, 'variable', ...newPathParts.slice(2, 4)];
    } else if (params.vaultAddress || params.vaultSlug) {
      newPathParts = [
      // oxlint-disable-next-line typescript/consistent-type-assertions
      newPathParts[0] as string, 'vault', ...newPathParts.slice(2, 4)];
    }
  }

  // Construct the new path
  const newPath = `/${newPathParts.join('/')}`;

  // Update search params with additional query parameters, if any
  const newSearchParams = includeCurrentSearchParams ? new URLSearchParams(searchParams.toString()) : new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (!PATH_PARAM_KEYS.has(key) && value) {
      newSearchParams.set(key, value);
    }
  });

  // Construct the full URL
  const queryString = newSearchParams.toString();
  return queryString ? `${newPath}?${queryString}` : newPath;
}

/**
 * Hook providing navigation utilities for the app, including URL generation and navigation.
 * Route-aware: on multi-chain pages, ignores chainIdentifier param to avoid corrupting the URL.
 * @returns A generateUrl function
 */
export function useGenerateUrl() {
  const $ = _c(3);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== pathname || $[1] !== searchParams) {
    t0 = (params, t1) => {
      const includeCurrentSearchParams = t1 === undefined ? true : t1;
      return generateUrlFromPathname({
        pathname,
        searchParams,
        params,
        includeCurrentSearchParams
      });
    };
    $[0] = pathname;
    $[1] = searchParams;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const generateUrl = t0;
  return generateUrl;
}